import { RichTextRenderer } from '@contember/react-client'
import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState, type CSSProperties, type FunctionComponent } from 'react'
import type { Background } from '../../generated/contember'
import type { CarouselResult } from '../data/CarouselFragment'
import type { ArticlesInputData } from '../utilities/ArticlesInput'
import s from './Carousel.module.sass'
import { ContemberImage } from './ContemberImage'
import { Icon } from './Icon'
import { Link } from './Link'
import { Video } from './Video'

import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import { CarouselNavigationSwiper } from './CarouselNavigationSwiper'
import type { Swiper as SwiperType } from 'swiper'

export type CarouselItem = CarouselResult['items'][number] | ArticlesInputData

export type CarouselProps = {
	title?: string
	text?: string
	squareImages?: boolean
	background?: Background
	items: CarouselItem[]
}

const SLIDE_GAP = 20

export const Carousel: FunctionComponent<CarouselProps> = ({ title, text, squareImages, background, items }) => {
	const swiperRef = useRef<SwiperType | null>(null)
	const [swiperReady, setSwiperReady] = useState(false)

	const updateTileDimensions = useCallback(() => {
		if (typeof window === 'undefined') {
			return
		}
		const swiper = swiperRef.current
		if (!swiper) {
			return
		}
		const container = swiper.el
		const element =
			Array.isArray(container) && container.length > 0 ? (container[0] as HTMLElement) : (container as HTMLElement)
		const computedStyle = window.getComputedStyle(element)
		const paddingLeft = parseFloat(computedStyle.paddingLeft || '0')
		const paddingRight = parseFloat(computedStyle.paddingRight || '0')
		const availableWidth = element.clientWidth - paddingLeft - paddingRight
		if (availableWidth <= 0) {
			return
		}
		const viewportWidth = window.innerWidth
		const perView = viewportWidth >= 639 ? 3 : viewportWidth >= 400 ? 1.5 : 1
		const perViewGapCount = Math.max(perView - 1, 0)
		const cardWidth = (availableWidth - perViewGapCount * SLIDE_GAP) / perView
		if (!Number.isFinite(cardWidth) || cardWidth <= 0) {
			return
		}
		element.style.setProperty('--Carousel-cardWidth', `${cardWidth}px`)
		element.style.setProperty('--Carousel-gap', `${SLIDE_GAP}px`)
		swiper.updateSlides()
		swiper.updateSlidesClasses()
		swiper.updateProgress()
	}, [])

	useEffect(() => {
		if (!swiperReady) {
			return
		}
		if (typeof window === 'undefined') {
			return
		}
		const swiper = swiperRef.current
		if (!swiper) {
			return
		}
		const handleResize = () => {
			updateTileDimensions()
		}
		swiper.on('resize', handleResize)
		window.addEventListener('resize', handleResize)
		let resizeObserver: ResizeObserver | undefined
		const container = swiper.el
		const element =
			Array.isArray(container) && container.length > 0 ? (container[0] as HTMLElement) : (container as HTMLElement)
		if (typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(() => {
				updateTileDimensions()
			})
			resizeObserver.observe(element)
		}

		return () => {
			swiper.off('resize', handleResize)
			window.removeEventListener('resize', handleResize)
			resizeObserver?.disconnect()
		}
	}, [swiperReady, updateTileDimensions])

	useEffect(() => {
		if (!swiperReady) {
			return
		}
		updateTileDimensions()
	}, [items.length, swiperReady, updateTileDimensions])

	return (
		<div
			className={clsx(
				s.Wrapper,
				background === 'red' && s.isRed,
				background === 'lightGrey' && s.isLight,
				background === 'lokal' && s.isLokalYellow,
			)}
		>
			<div className={s.WrapperIn}>
				<div className={s.TitleWrapper}>
					<h3 className={s.Title}>{title}</h3>
					{text && (
						<div className={s.Text}>
							<RichTextRenderer source={text} />
						</div>
					)}
				</div>
				<Swiper
					slidesPerView="auto"
					initialSlide={0}
					loop={false}
					className={s.Carousel}
					spaceBetween={SLIDE_GAP}
					onSwiper={(instance) => {
						swiperRef.current = instance
						setSwiperReady(true)
						updateTileDimensions()
					}}
					style={{ '--Carousel-slideCount': items.length, '--Carousel-gap': `${SLIDE_GAP}px` }}
				>
					{items.map((item) => {
						const carouselItem = isCarouselResultItem(item) ? item : null
						const image = item.image
						const video = carouselItem?.video
						const hasImage = Boolean(image)
						const hasVideo = Boolean(video?.src)
						const mediaType: CarouselMediaContext['mediaType'] =
							hasVideo && (!hasImage || carouselItem?.isImage === false)
								? 'video'
								: hasImage
									? 'image'
									: hasVideo
										? 'video'
										: null
						const normalAspectRatio = Boolean(carouselItem?.normalAspectRatio)
						const aspectRatioSource = mediaType === 'video' ? video : mediaType === 'image' ? image : undefined
						const mediaRatio =
							aspectRatioSource?.width && aspectRatioSource?.height
								? aspectRatioSource.width / aspectRatioSource.height
								: undefined
						const slideStyle: CSSProperties | undefined =
							normalAspectRatio && mediaRatio ? { '--Carousel-mediaRatio': mediaRatio } : undefined
						const media: CarouselMediaContext = { mediaType, image, video, normalAspectRatio }

						return 'link' in item ? (
							<SwiperSlide
								key={item.id}
								className={clsx(s.Slide, normalAspectRatio && s.NormalAspectSlide)}
								style={slideStyle}
							>
								<Link href={`https://jidloaradost.vercel.app/${item.link.url}`} className={s.CarouselLinkItem}>
									<CarouselItemContent item={item} squareImages={squareImages} media={media} />
								</Link>
							</SwiperSlide>
						) : (
							<SwiperSlide
								key={item.id}
								className={clsx(s.Slide, normalAspectRatio && s.NormalAspectSlide)}
								style={slideStyle}
							>
								{item.linkUrl ? (
									<Link link={item.linkUrl} className={s.CarouselLinkItem}>
										<CarouselItemContent item={item} squareImages={squareImages} media={media} />
									</Link>
								) : (
									<CarouselItemContent item={item} squareImages={squareImages} media={media} />
								)}
							</SwiperSlide>
						)
					})}
					{items.length > 1 && (
						<div
							style={{
								marginTop: '20px',
							}}
						>
							<CarouselNavigationSwiper />
						</div>
					)}
				</Swiper>
			</div>
		</div>
	)
}

const isCarouselResultItem = (item: CarouselItem): item is CarouselResult['items'][number] => 'linkUrl' in item

type CarouselMediaContext = {
	mediaType: 'image' | 'video' | null
	image?: CarouselProps['items'][number]['image']
	video?: CarouselResult['items'][number]['video']
	normalAspectRatio: boolean
}

export const CarouselItemContent: FunctionComponent<{
	item: CarouselProps['items'][number]
	squareImages: CarouselProps['squareImages']
	media: CarouselMediaContext
}> = ({ item, squareImages, media }) => {
	const { mediaType, image, video, normalAspectRatio } = media

	const wrapperClassName = clsx(
		s.CarouselImage,
		mediaType === 'image' && squareImages && !normalAspectRatio && s.SquareImages,
		normalAspectRatio && s.NormalAspectRatio,
	)

	return (
		<>
			{mediaType && (
				<div className={wrapperClassName}>
					{mediaType === 'image' && image && <ContemberImage image={image} layout="fill" objectFit="cover" />}
					{mediaType === 'video' && video && <Video video={video} />}
				</div>
			)}
			<p className={s.CarouselTitle}>{item.title}</p>
			{'text' in item && item.text && (
				<div className={s.CarouselText}>
					<RichTextRenderer source={item.text} />
				</div>
			)}
			{'leadParagraphForTiles' in item && <div className={s.CarouselText}>{item.leadParagraphForTiles}</div>}
			{'name' in item && <p className={s.CarouselName}>{item.name}</p>}
			{'views' in item && (
				<div className={s.CarouselViewsWrapper}>
					<span className={s.CarouselViewsIcon}>
						<Icon name="read" />
					</span>
					<p className={s.CarouselName}>{item.views}</p>
				</div>
			)}
		</>
	)
}
